import { isDatabaseUnavailableError } from '../../shared/localDb';
import {
  buildCashSessionPaymentBreakdown,
  type CashSummary,
} from '../../cash/domain-api';
import type { SalesByCashierRow, SalesByTimeBandRow, SalesComparison, SalesSnapshot } from '../../sales/public-api';
import type { IntelligenceKpisResponse } from '../domain/reportsIntelligenceTypes';
import type {
  BranchCashSessionReportRow,
  PosExceptionSummary,
  ReportsOperacionKpiSources,
  SalesDailyRow,
} from '../domain/reportsTypes';
import { reportsQueryScopeHasFilters, type ReportsQueryScope } from '../domain/reportsQueryScope';
import type {
  LoadReportsOperacionDashboardParams,
  ReportsOperacionDashboardDeps,
} from './reportsOperacionDashboardTypes';

export const emptySalesSnapshot = (): SalesSnapshot => ({
  salesCount: 0,
  salesTotal: 0,
  fiscalSalesCount: 0,
  fiscalSalesTotal: 0,
  nonFiscalSalesCount: 0,
  nonFiscalSalesTotal: 0,
  pendingPayments: 0,
  pendingPaymentsAmount: 0,
  paymentBreakdown: [],
});

export const emptySalesComparison = (): SalesComparison => ({
  todayTotal: 0,
  todayCount: 0,
  yesterdayTotal: 0,
  yesterdayCount: 0,
  sameDayLastWeekTotal: 0,
  sameDayLastWeekCount: 0,
});

export const emptyCashSummary = (): CashSummary => ({
  activeSession: null,
  todaySalesCount: 0,
  todaySalesTotal: 0,
  todayCashTotal: 0,
  sessionCashTotal: 0,
  sessionSalesCount: 0,
  sessionSalesTotal: 0,
  sessionTipsTotal: 0,
  sessionPaymentBreakdown: buildCashSessionPaymentBreakdown([]),
  sessionOutflowsTotal: 0,
  sessionInflowsTotal: 0,
  todayDigitalTotal: 0,
  lastClosedSession: null,
  expectedBalance: null,
});

export const defaultKpiSources = (): ReportsOperacionKpiSources => ({
  cash: 'device',
  salesRange: 'device',
  comparison: 'device',
  timeBands: 'device',
  cashSessions: 'device',
});

export type LocalReaders = {
  readLocalCashSummary: () => Promise<CashSummary>;
  readLocalSalesSnapshot: (from: string, to: string) => Promise<SalesSnapshot>;
  readLocalTodaySnapshot: () => Promise<SalesSnapshot>;
  readLocalComparison: () => Promise<SalesComparison>;
  readLocalBands: () => Promise<SalesByTimeBandRow[]>;
};

export function createLocalReaders(
  branchId: string,
  reportsQueryScope: ReportsQueryScope,
  dateForBandsIsoResolved: string,
  deps: ReportsOperacionDashboardDeps
): LocalReaders {
  const readLocalCashSummary = async (): Promise<CashSummary> => {
    try {
      return await deps.getCashSummary(branchId);
    } catch (error) {
      if (isDatabaseUnavailableError(error)) return emptyCashSummary();
      throw error;
    }
  };

  const readLocalSalesSnapshot = async (from: string, to: string): Promise<SalesSnapshot> => {
    try {
      return await deps.getSalesSnapshotByDateRangeWithVentasScope(branchId, from, to, reportsQueryScope);
    } catch (error) {
      if (isDatabaseUnavailableError(error)) return emptySalesSnapshot();
      throw error;
    }
  };

  const readLocalTodaySnapshot = async (): Promise<SalesSnapshot> => {
    try {
      return await deps.getTodaySalesSnapshot(branchId);
    } catch (error) {
      if (isDatabaseUnavailableError(error)) return emptySalesSnapshot();
      throw error;
    }
  };

  const readLocalComparison = async (): Promise<SalesComparison> => {
    try {
      return await deps.getSalesComparison(branchId, reportsQueryScope);
    } catch (error) {
      if (isDatabaseUnavailableError(error)) return emptySalesComparison();
      throw error;
    }
  };

  const readLocalBands = async (): Promise<SalesByTimeBandRow[]> => {
    try {
      return await deps.getSalesByTimeBand(branchId, dateForBandsIsoResolved, reportsQueryScope);
    } catch (error) {
      if (isDatabaseUnavailableError(error)) return [];
      throw error;
    }
  };

  return {
    readLocalCashSummary,
    readLocalSalesSnapshot,
    readLocalTodaySnapshot,
    readLocalComparison,
    readLocalBands,
  };
}

export type OnlineFetchResult = {
  cashData: CashSummary;
  todaySalesData: SalesSnapshot;
  periodSalesData: SalesSnapshot;
  previousPeriodSalesData: SalesSnapshot | null;
  comparison: SalesComparison;
  bands: SalesByTimeBandRow[];
  byCashier: SalesByCashierRow[];
  dailySeries: SalesDailyRow[] | null;
  kpiSources: ReportsOperacionKpiSources;
  branchCashSessions: BranchCashSessionReportRow[];
  posExceptionSummary: PosExceptionSummary | null;
  salesByCashierSource: 'server' | 'device';
};

export async function fetchOnlineDashboard(
  params: LoadReportsOperacionDashboardParams,
  deps: ReportsOperacionDashboardDeps,
  readers: LocalReaders
): Promise<OnlineFetchResult> {
  const {
    branchId,
    todayRange,
    periodRange,
    dateForBandsIsoResolved,
    bandsApplicable,
    prevPeriodRange,
    reportsQueryScope,
    todayStartIso,
    dateRangePresetIsToday,
  } = params;
  const { fromIso, toIso } = periodRange;

  const periodSameAsToday = todayRange.fromIso === fromIso && todayRange.toIso === toIso;
  const scopeHasFilters = reportsQueryScopeHasFilters(reportsQueryScope);
  const canReuseTodaySnapForPeriod = periodSameAsToday && !scopeHasFilters;

  const remoteCashP = deps.fetchWithRetry(() => deps.fetchRemoteBranchCashSummary(branchId, todayStartIso));
  const remoteTodaySnapP = deps.fetchWithRetry(() =>
    deps.fetchRemoteSalesSnapshot(branchId, todayRange.fromIso, todayRange.toIso)
  );
  const remotePeriodSnapP = canReuseTodaySnapForPeriod
    ? remoteTodaySnapP
    : deps.fetchWithRetry(() =>
        deps.fetchRemoteSalesSnapshot(branchId, fromIso, toIso, reportsQueryScope)
      );
  const remotePrevSnapP =
    prevPeriodRange != null
      ? deps.fetchWithRetry(() =>
          deps.fetchRemoteSalesSnapshot(
            branchId,
            prevPeriodRange.fromIso,
            prevPeriodRange.toIso,
            reportsQueryScope
          )
        )
      : Promise.resolve(null);
  const remoteCompP = deps.fetchWithRetry(() => deps.fetchRemoteSalesComparison(branchId));
  const remoteBandsP = bandsApplicable
    ? deps.fetchWithRetry(() =>
        deps.fetchRemoteSalesTimeBands(branchId, dateForBandsIsoResolved, reportsQueryScope)
      )
    : Promise.resolve(null);
  const remoteDailyP = deps.fetchWithRetry(() =>
    deps.fetchRemoteSalesDaily(branchId, fromIso, toIso, reportsQueryScope)
  );
  const remoteByCashierP = deps.fetchWithRetry(() =>
    deps.fetchRemoteSalesByCashier(branchId, fromIso, toIso, reportsQueryScope)
  );
  const remoteCashSessionsP = deps.fetchWithRetry(() =>
    deps.fetchRemoteBranchCashSessions(branchId, fromIso, toIso)
  );
  const remotePosExceptionsP = deps.fetchWithRetry(() =>
    deps.fetchRemotePosExceptionSummary(branchId, fromIso, toIso)
  );

  const [remoteCash, remoteTodaySnap, remotePeriodSnap, remotePrevSnap] = await Promise.all([
    remoteCashP,
    remoteTodaySnapP,
    remotePeriodSnapP,
    remotePrevSnapP,
  ]);

  const [
    remoteComp,
    remoteBands,
    remoteDaily,
    remoteByCashier,
    remoteCashSessions,
    remotePosExceptions,
  ] = await Promise.all([
    remoteCompP,
    remoteBandsP,
    remoteDailyP,
    remoteByCashierP,
    remoteCashSessionsP,
    remotePosExceptionsP,
  ]);

  const kpiSources = defaultKpiSources();

  const cashData = remoteCash ?? (await readers.readLocalCashSummary());
  kpiSources.cash = remoteCash ? 'server' : 'device';

  const todaySalesData = remoteTodaySnap ?? (await readers.readLocalTodaySnapshot());
  const periodSalesData =
    remotePeriodSnap ??
    (dateRangePresetIsToday && !scopeHasFilters
      ? todaySalesData
      : await readers.readLocalSalesSnapshot(fromIso, toIso));
  kpiSources.salesRange = remotePeriodSnap ? 'server' : 'device';

  const previousPeriodSalesData =
    prevPeriodRange != null
      ? remotePrevSnap ?? (await readers.readLocalSalesSnapshot(prevPeriodRange.fromIso, prevPeriodRange.toIso))
      : null;

  const comparison = remoteComp ?? (await readers.readLocalComparison());
  kpiSources.comparison = remoteComp ? 'server' : 'device';

  const bands = bandsApplicable
    ? (remoteBands ?? (await readers.readLocalBands()))
    : [];
  kpiSources.timeBands = bandsApplicable ? (remoteBands ? 'server' : 'device') : 'device';

  const dailySeries = remoteDaily;
  // Owner: Informes `fetchRemoteSalesByCashier` → `reports_branch_sales_by_cashier`.
  // Sin fallback sales stub (legacy-noop-prevention): null remoto = ranking vacío + source device.
  const byCashier = remoteByCashier ?? [];
  const salesByCashierSource: 'server' | 'device' = remoteByCashier != null ? 'server' : 'device';
  const branchCashSessions = remoteCashSessions ?? [];
  const posExceptionSummary = remotePosExceptions;
  kpiSources.cashSessions = remoteCashSessions ? 'server' : 'device';

  return {
    cashData,
    todaySalesData,
    periodSalesData,
    previousPeriodSalesData,
    comparison,
    bands,
    byCashier,
    dailySeries,
    kpiSources,
    branchCashSessions,
    posExceptionSummary,
    salesByCashierSource,
  };
}

export type OfflineFetchResult = {
  cashData: CashSummary;
  todaySalesData: SalesSnapshot;
  periodSalesData: SalesSnapshot;
  previousPeriodSalesData: SalesSnapshot | null;
  comparison: SalesComparison;
  bands: SalesByTimeBandRow[];
  byCashier: SalesByCashierRow[];
  dailySeries: SalesDailyRow[] | null;
  kpiSources: ReportsOperacionKpiSources;
  branchCashSessions: BranchCashSessionReportRow[];
  posExceptionSummary: PosExceptionSummary | null;
  salesByCashierSource: 'server' | 'device';
};

export async function fetchOfflineDashboard(
  params: LoadReportsOperacionDashboardParams,
  deps: ReportsOperacionDashboardDeps,
  readers: LocalReaders
): Promise<OfflineFetchResult> {
  const {
    branchId,
    periodRange,
    bandsApplicable,
    prevPeriodRange,
    reportsQueryScope,
    dateRangePresetIsToday,
  } = params;
  const { fromIso, toIso } = periodRange;
  const scopeHasFilters = reportsQueryScopeHasFilters(reportsQueryScope);

  const cashData = await readers.readLocalCashSummary();
  const todaySalesData = await readers.readLocalTodaySnapshot();
  const periodSalesData =
    dateRangePresetIsToday && !scopeHasFilters
      ? todaySalesData
      : await readers.readLocalSalesSnapshot(fromIso, toIso);
  const previousPeriodSalesData =
    prevPeriodRange != null
      ? await readers.readLocalSalesSnapshot(prevPeriodRange.fromIso, prevPeriodRange.toIso)
      : null;
  const comparison = await readers.readLocalComparison();
  const bands = bandsApplicable ? await readers.readLocalBands() : [];
  const byCashier: SalesByCashierRow[] = [];

  return {
    cashData,
    todaySalesData,
    periodSalesData,
    previousPeriodSalesData,
    comparison,
    bands,
    byCashier,
    dailySeries: null,
    kpiSources: defaultKpiSources(),
    branchCashSessions: [],
    posExceptionSummary: null,
    salesByCashierSource: 'device',
  };
}
